import { CustomTable, setNotification, ETypeOfNotification } from "medere-ui-framework"
import { useIntl } from "react-intl"
import { useRef, useState } from "react"
import { Column } from "primereact/column"
import { useSearchParams } from "react-router"
import { useDispatch } from "react-redux";
import { useHospitalizationService } from "../servicesHooks/Hospitalization/useHospitalizationService"


interface ValuationReportWithMedicationWindowProps {
    healthPatientServiceId?: number;
    initialDateTime?: Date;
    finalDateTime?: Date;
    name?: string | null;
    serviceEnded?: boolean;
}

function ValuationReportWithMedicationWindow(props: ValuationReportWithMedicationWindowProps) {
    const { getValuationOfPatientComplete, exportValuationOfPatientCompleteReport } = useHospitalizationService();

    const [searchParams] = useSearchParams();

    const healthPatientServiceId: number = props.healthPatientServiceId ?? Number(searchParams.get('healthPatientServiceID'));
    const initialDateTime: Date = props.initialDateTime ?? new Date(searchParams.get('initialDateTime') || '');
    const finalDateTime: Date = props.finalDateTime ?? new Date(searchParams.get('finalDateTime') || '');
    const name: string | null = props.name ?? searchParams.get('name');
    const serviceEnded: boolean = props.serviceEnded ?? (searchParams.get('showProfessionals') === 'true');
    const generatingNotifiedRef = useRef(false);
    const dispatch = useDispatch();
    const [itemCount, setItemCount] = useState<number | null>(null);

    const MAX_WAIT_MS = 7 * 60 * 1000;
    const POLL_INTERVAL = 2000;

    const tableRef = useRef(null)
    const intl = useIntl();

    const formattedFromDate = initialDateTime
        ? initialDateTime.toLocaleDateString('es-AR')
        : "";

    const formattedToDate = finalDateTime
        ? finalDateTime.toLocaleDateString('es-AR')
        : "";

    const getValuationForTable = async (
        offset: number,
        pageSize: number,
        params: any,
        startedAt = Date.now()
    ): Promise<any[]> => {

        const response = await getValuationOfPatientComplete(offset, pageSize, params);

        if (response.status === 202) {

            if (!generatingNotifiedRef.current) {
                generatingNotifiedRef.current = true;

                dispatch(setNotification({
                    title: "Generando reporte",
                    desc: "Espere mientras se genera la tabla. Puede tardar unos minutos.",
                    typeOfNotification: ETypeOfNotification.INFO,
                    icon: "pi pi-info-circle",
                    duration: 8000
                }));
            }

            if (Date.now() - startedAt > MAX_WAIT_MS) {
                generatingNotifiedRef.current = false;

                dispatch(setNotification({
                    title: "Tiempo excedido",
                    desc: "El reporte tardó demasiado en generarse.",
                    typeOfNotification: ETypeOfNotification.ERROR,
                    icon: "pi pi-times-circle",
                    duration: 10000
                }));

                throw new Error("El reporte tardó demasiado en generarse");
            }

            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
            return getValuationForTable(offset, pageSize, params, startedAt);
        }

        generatingNotifiedRef.current = false;

        const data: any[] = response.data ?? [];
        setItemCount(prev => offset === 0 ? data.length : (prev ?? 0) + data.length);
        return data;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {/* Info bar */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                alignItems: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #DEE2E6',
                borderRadius: '6px',
                padding: '10px 16px',
            }}>
                {name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="pi pi-user" style={{ color: '#183462', fontSize: '14px' }} />
                        <span style={{ fontSize: '14px', color: '#6C757D', fontWeight: 500 }}>
                            {intl.formatMessage({ id: 'Patient' })}:
                        </span>
                        <span style={{ fontSize: '14px', color: '#183462', fontWeight: 600 }}>
                            {name}
                        </span>
                    </div>
                )}

                {(formattedFromDate || formattedToDate) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="pi pi-calendar" style={{ color: '#183462', fontSize: '14px' }} />
                        <span style={{ fontSize: '14px', color: '#6C757D', fontWeight: 500 }}>
                            {intl.formatMessage({ id: 'since' })}:
                        </span>
                        <span style={{ fontSize: '14px', color: '#495057' }}>
                            {formattedFromDate}
                        </span>
                        <span style={{ fontSize: '14px', color: '#6C757D' }}>—</span>
                        <span style={{ fontSize: '14px', color: '#495057' }}>
                            {formattedToDate}
                        </span>
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                    <i className="pi pi-list" style={{ color: '#183462', fontSize: '14px' }} />
                    <span style={{ fontSize: '14px', color: '#6C757D', fontWeight: 500 }}>Items:</span>
                    <span style={{
                        fontSize: '14px',
                        color: '#183462',
                        fontWeight: 700,
                        minWidth: '24px',
                        textAlign: 'right',
                    }}>
                        {itemCount ?? '—'}
                    </span>
                </div>
            </div>

            {/* Table */}
            <CustomTable
                idTable={'valuationCompleteTable'}
                ref={tableRef}
                showGridlines
                hideFiltersButton
                notFlex
                maxHeight={700}
                getItems={getValuationForTable}
                params={{
                    healthPatientServiceId,
                    initialDateTime,
                    finalDateTime,
                    serviceEnded
                }}
                exportItems={[
                    { label: "Excel", icon: "pi-file-excel", onExport: exportValuationOfPatientCompleteReport }
                ]}
                className='crudTable'
            >
                <Column
                    field="prescriptiondate"
                    header="Fecha prescripción"
                    className="column"
                    style={{ minWidth: '140px' }}
                    body={(record) => {
                        if (!record.prescriptiondate) return "";
                        const d = new Date(record.prescriptiondate);
                        return isNaN(d.getTime()) ? "" : d.toLocaleDateString('es-AR', { timeZone: 'UTC' });
                    }}
                />
                <Column
                    field="item"
                    header="Item prescripto"
                    className="column"
                    style={{ minWidth: '180px' }}
                />
                <Column
                    field="codebar"
                    header="Código de barras"
                    className="column"
                    style={{ minWidth: '140px' }}
                />
                <Column
                    field="type"
                    header="Tipo"
                    className="column"
                    style={{ minWidth: '100px' }}
                />
                <Column
                    field="service"
                    header="Servicio"
                    className="column"
                    style={{ minWidth: '120px' }}
                />
                <Column
                    field="qty"
                    header="Cantidad"
                    className="column"
                    style={{ minWidth: '90px', textAlign: 'right' }}
                    headerStyle={{ textAlign: 'right' }}
                    bodyStyle={{ textAlign: 'right' }}
                />
                <Column
                    field="unitprice"
                    header="Precio unitario"
                    className="column"
                    style={{ minWidth: '120px' }}
                    headerStyle={{ textAlign: 'right' }}
                    bodyStyle={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                    body={(record) =>
                        record.unitprice != null
                            ? Number(record.unitprice).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : ""
                    }
                />
                <Column
                    field="total"
                    header="Precio total"
                    className="column"
                    style={{ minWidth: '120px' }}
                    headerStyle={{ textAlign: 'right' }}
                    bodyStyle={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#183462' }}
                    body={(record) =>
                        record.total != null
                            ? Number(record.total).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : ""
                    }
                />
            </CustomTable>
        </div>
    )
}

export default ValuationReportWithMedicationWindow;
